import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';

import { StyledTable, StyledTableCell, StyledTableHeader, StyledTableRow } from './extensions/TableStyled';
import { FontSize } from './extensions/FontSize';
import { LineHeight } from './extensions/LineHeight';
import { Indent } from './extensions/Indent';
import { PageBreak } from './extensions/PageBreak';
import { Pagination } from './extensions/Pagination';
import { ClipboardClean } from './extensions/ClipboardClean';
import { TableSelectAll } from './extensions/TableSelectAll';
import { TableFit } from './extensions/TableFit';
import { ImageSelect } from './extensions/ImageSelect';
import { ParagraphSpacing } from './extensions/ParagraphSpacing';
import { ListStyle } from './extensions/ListStyle';
import { Extension } from '@tiptap/core';

/** Word alignment shortcuts (Ctrl+L / E / R / J), matching the ribbon tooltips.
 *  High priority so Ctrl+E aligns centre instead of toggling inline code, and
 *  so the browser's own Ctrl+J / Ctrl+L / Ctrl+R / Ctrl+E actions don't fire
 *  while typing in the editor. */
const AlignShortcuts = Extension.create({
  name: 'alignShortcuts',
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      'Mod-l': () => this.editor.commands.setTextAlign('left'),
      'Mod-e': () => this.editor.commands.setTextAlign('center'),
      'Mod-r': () => this.editor.commands.setTextAlign('right'),
      'Mod-j': () => this.editor.commands.setTextAlign('justify')
    };
  }
});

export function buildExtensions() {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      codeBlock: { HTMLAttributes: { class: 'code-block' } },
      horizontalRule: { HTMLAttributes: { class: 'hr' } }
    }),
    Underline,
    Subscript,
    Superscript,
    // mergeNestedSpanStyles: Word nests <span style="font-size…"><span
    // style="color…">; without merging, the inner span's mark replaced the
    // outer one and the pasted text lost its font/size.
    TextStyle.configure({ mergeNestedSpanStyles: true }),
    Color.configure({ types: ['textStyle'] }),
    FontFamily.configure({ types: ['textStyle'] }),
    FontSize,
    LineHeight,
    Indent,
    ParagraphSpacing,
    ListStyle,
    AlignShortcuts,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' }
    }),
    Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'we-image' } }),
    Placeholder.configure({
      placeholder: ({ node }) => (node.type.name === 'heading' ? 'Heading' : 'Start typing your document…')
    }),
    Typography,
    TaskList,
    TaskItem.configure({ nested: true }),
    StyledTable.configure({
      resizable: true,
      lastColumnResizable: true,
      allowTableNodeSelection: true,
      HTMLAttributes: { class: 'we-table' }
    }),
    StyledTableRow,
    StyledTableHeader,
    StyledTableCell,
    PageBreak,
    Pagination,
    ClipboardClean,
    TableSelectAll,
    TableFit,
    ImageSelect
  ];
}
